import { NavLink } from 'react-router-dom';
import {
  Activity,
  Calendar,
  LayoutGrid,
  LogOut,
  Search,
  Settings,
  ShoppingCart,
  Utensils,
} from 'lucide-react';
import { useAuth } from '../stores/auth.js';
import { useUi } from '../stores/ui.js';
import { MessagesBell } from './MessagesBell.js';
import { PendingBadge } from './PendingBadge.js';
import { tabs as bottomNavTabs } from '../ui/BottomNav.js';
import { cn } from '../lib/cn.js';

interface NavEntry {
  to: string;
  label: string;
  icon: React.ReactNode;
}

// Les cinq destinations quotidiennes, et rien d'autre : le Catalogue vit dans Courses,
// les projets derrière Settings — cf. ADR 0007.
const NAV: NavEntry[] = [
  { to: '/today', label: 'Today', icon: <Calendar size={16} aria-hidden /> },
  { to: '/board', label: 'Upcoming', icon: <LayoutGrid size={16} aria-hidden /> },
  { to: '/courses', label: 'Courses', icon: <ShoppingCart size={16} aria-hidden /> },
  { to: '/tracker', label: 'Tracker', icon: <Activity size={16} aria-hidden /> },
  { to: '/calories', label: 'Calories', icon: <Utensils size={16} aria-hidden /> },
];

// Sur mobile le drawer ne porte que ce que la barre d'onglets n'atteint pas. La liste
// des onglets est importée plutôt que recopiée : ajouter un onglet le retire du drawer
// sans qu'on ait à y penser.
const BOTTOM_NAV_ROUTES = new Set<string>(bottomNavTabs.map((t) => t.to));

// `.navigation` du kit — dense sur un bureau, à la taille du doigt dans le tiroir. NavLink pose
// `aria-current="page"` sur la destination en cours : c'est l'attribut que le kit habille.
export function SidebarContent({
  mobile = false,
  onNavigate,
}: {
  mobile?: boolean;
  onNavigate?: (() => void) | undefined;
}): React.ReactElement {
  const logout = useAuth((s) => s.logout);
  const openSearch = useUi((s) => s.openSearch);
  const openSettings = useUi((s) => s.openSettings);

  const entries = mobile ? NAV.filter((e) => !BOTTOM_NAV_ROUTES.has(e.to)) : NAV;
  const dense = !mobile && 'navigation-dense';

  return (
    <>
      <div className="px-4 py-4">
        {/* Le mot en encre de page, le point en braise (Braise 2) : la marque rouge reste aux
            actions, le logo n'a pas à crier. */}
        <strong className="font-display text-2xl">
          nota<span className="text-now">.</span>
        </strong>
      </div>

      <nav className={cn('navigation px-2 flex-1 overflow-y-auto', dense)} aria-label="Navigation">
        {!mobile && (
          <button type="button" onClick={() => openSearch()} className="navigation-lien">
            <Search size={16} aria-hidden />
            Search
            <kbd className="touche">/</kbd>
          </button>
        )}
        <PendingBadge variant="row" />
        <MessagesBell variant="row" onNavigate={onNavigate} />
        {entries.map((e) => (
          <NavLink key={e.to} to={e.to} onClick={onNavigate} className="navigation-lien">
            {e.icon}
            {e.label}
          </NavLink>
        ))}
      </nav>

      <div className={cn('navigation p-2', dense, mobile && 'border-t border-edge')}>
        <button
          type="button"
          onClick={() => {
            openSettings();
            onNavigate?.();
          }}
          className="navigation-lien"
        >
          <Settings size={16} aria-hidden />
          Settings
        </button>
        <button type="button" onClick={logout} className="navigation-lien">
          <LogOut size={16} aria-hidden />
          Logout
        </button>
      </div>
    </>
  );
}
